import { LoginPanel } from '~/app/auth/login-panel';
import { LandingFooter, LandingNav } from '~/app/landing/landing-chrome';
import { LandingShell } from '~/app/landing/landing-shell';
import SEO from '~/components/seo';
import { CANONICAL_ORIGIN } from '~/constants';
import React from 'react';

const Product: React.FC<{
  image: string;
  name: string;
  href: string;
  theme: 'versbox' | 'woyti';
  children: React.ReactNode;
}> = ({ image, name, href, theme, children }) => {
  const styles =
    theme === 'versbox'
      ? {
          card: 'border-purple-600/30 bg-white hover:border-purple-700 hover:shadow-[0_20px_40px_-24px_rgba(155,81,224,0.45)]',
          title: 'group-hover:text-[#9b51e0]',
          link: 'text-[#9b51e0] group-hover:text-[#201c6a]',
          badge: 'bg-[#9b51e0]/10 text-[#201c6a]',
          label: 'Versbox',
        }
      : {
          card: 'border-green-600/40 bg-white hover:border-green-700 hover:shadow-[0_20px_40px_-24px_rgba(72,164,63,0.45)]',
          title: 'group-hover:text-[#48a43f]',
          link: 'text-[#48a43f] group-hover:text-[#3c8c34]',
          badge: 'bg-[#48a43f]/10 text-[#265c21]',
          label: 'Woyti',
        };

  return (
    <article className={`group rounded-2xl border p-8 transition hover:-translate-y-1 ${styles.card}`}>
      <a href={href} className="block outline-none">
        <div className="flex items-center justify-center h-32 mb-6">
          {theme === 'woyti' ? (
            <div className="flex items-center gap-2.5">
              <img alt="" className="h-14 w-14 object-contain" src={image} />
              <span className="landing-display text-3xl tracking-tight text-[#48a43f]">WOYTI</span>
            </div>
          ) : (
            <img alt="" className="max-h-20 max-w-[12rem] w-auto h-auto object-contain" src={image} />
          )}
        </div>
        <h3 className={`landing-display text-xl text-[#27272a] transition-colors ${styles.title}`}>{name}</h3>
      </a>
      <div className="mt-3 landing-ink-soft leading-relaxed text-sm">{children}</div>
      <a
        href={href}
        className={`mt-6 inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${styles.link}`}
      >
        Meer informatie
        <span aria-hidden className="transition-transform duration-300 group-hover:translate-x-0.5">
          →
        </span>
      </a>
    </article>
  );
};

export const LandingPage: React.FC = () => {
  return (
    <LandingShell className="min-h-screen flex flex-col landing-cream">
      <SEO canonicalUrl={CANONICAL_ORIGIN} />

      <LandingNav />

      <main className="flex-1">
        <section className="bg-white">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16 lg:py-20">
            <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div className="min-w-0">
                <p className="landing-rise inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-800">
                  <span className="h-1.5 w-1.5 rounded-full bg-blue-500" aria-hidden />
                  Platform voor handelaars
                </p>
                <h1 className="landing-hero-title landing-rise-delay mt-5 text-4xl md:text-5xl lg:text-6xl">
                  <span className="block">
                    Beheer <span className="text-blue-500">slimmer</span>.
                  </span>
                  <span className="block">Verkoop méér.</span>
                </h1>
                <p className="landing-rise-delay landing-muted mt-5 text-base leading-[1.6] md:text-lg">
                  Log in om je organisaties, winkels en lockers te beheren.
                </p>
              </div>

              <div id="login" className="landing-rise-late flex md:justify-end scroll-mt-6">
                <LoginPanel variant="landing" />
              </div>
            </div>
          </div>
        </section>

        <section id="producten" className="landing-cream border-t border-blue-100/80">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
            <div className="max-w-xl mb-10 sm:mb-14">
              <p className="text-xs font-medium uppercase tracking-widest text-blue-800 mb-3">Producten</p>
              <h2 className="landing-hero-title text-3xl md:text-4xl">Ontdek wat bij jouw zaak past</h2>
              <p className="mt-3 landing-muted leading-relaxed">
                Viresmo verbindt lokale versspecialisten met hun klanten — via afhaal en dagverse aanbiedingen.
              </p>
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <Product theme="versbox" href="https://versbox.be/" name="De Versbox" image="/assets/logo_versbox.svg">
                <p>
                  Een afhaalsysteem dat gekoppeld kan worden aan een webshop, de Woyti-app of zelfs volledig
                  onafhankelijk gebruikt kan worden.
                </p>
                <p className="mt-2">
                  Klanten halen 24/7 af op een moment dat zij kiezen, met de zekerheid van verse en kwalitatieve
                  producten.
                </p>
              </Product>
              <Product theme="woyti" href="https://www.woyti.eu/" name="Woyti" image="/assets/logo_woyti.svg">
                <p>
                  Een app als digitale folder voor je klanten. Met één klik krijgt iedereen een melding: kopen, afhalen
                  en klaar!
                </p>
              </Product>
            </div>
          </div>
        </section>
      </main>

      <LandingFooter />
    </LandingShell>
  );
};
